import { message } from 'antd';
import moment from 'moment';
import numeral from 'numeral';
import {  DEFUALT_SPLIT_NUMBER } from '@/infrastructure/echart-config';
import { getMetricEChartOptions } from '@/infrastructure/echart-config/chart-option-metric';
import { logger } from '@/infrastructure/logger';
import { getProductId } from '@/infrastructure/product-info';
import { formatNum } from '@/infrastructure/utils/digit';
import { DATE_TIME_FORMAT } from '@/infrastructure/utils/timestamp';
import { formatCount } from '@/infrastructure/utils/to-unit';
import { RATE, SYMBOL } from '@/presentation/components/issue-trend-analysis/line-chart';
import { MetricName } from '@/presentation/components/trend/api';
import { IGetDashboardDataReq, IGetDashboardDataRsp, MetricSubType, MetricType, TimeType } from '@/proto/metric';
import { metricService } from '@/service/metric';
import { fromPBTime } from '@raft/utils';

const formatByte2Mb = (num: number |string): string => {
  if (!num || isNaN(Number(num))) return '0.0000';

  const mb = (Number(num) / 1024 / 1024);

  return numeral(mb).format('0.0000');
};
export const getChartOptionStats = async (
  metricSubTypes: MetricSubType,
  timeWindow: TimeType,
  token: string,
  showCount = true,
) => {
  const dash: {
    avg: string[],
    p25: string[],
    p50: string[],
    p75: string[],
    p90: string[],
    p99: string[],
  } = {
    avg: [],
    p25: [],
    p50: [],
    p75: [],
    p90: [],
    p99: [],
  };
  let count: number[] = [];
  let timePoints: string[] = [];

  const loadData = async () => {
    const req: IGetDashboardDataReq = {
      productId: getProductId(),
      type: MetricType.TYPE_TRAFFIC_RESOURCE,
      subTypes: [metricSubTypes],
      token,
    };
    const data = await metricService.loadDashboard(req);
    count = data.data.find(item => item.metricName === 'dau_count')
      ?.dataPoints?.map(item => (isNaN(Number(item)) ? 0 : Number(item))) ?? [];
    timePoints = data.timePoints.map(item => moment(item).format(DATE_TIME_FORMAT));
    dash.avg = data.data.find(item => item.metricName === 'traffic_avg')?.dataPoints?.map(item => formatByte2Mb(item)) ?? [];
    dash.p25 = data.data.find(item => item.metricName === 'traffic_p25')?.dataPoints?.map(item => formatByte2Mb(item)) ?? [];
    dash.p50 = data.data.find(item => item.metricName === 'traffic_p50')?.dataPoints?.map(item => formatByte2Mb(item)) ?? [];
    dash.p75 = data.data.find(item => item.metricName === 'traffic_p75')?.dataPoints?.map(item => formatByte2Mb(item)) ?? [];
    dash.p90 = data.data.find(item => item.metricName === 'traffic_p90')?.dataPoints?.map(item => formatByte2Mb(item)) ?? [];
    dash.p99 = data.data.find(item => item.metricName === 'traffic_p99')?.dataPoints?.map(item => formatByte2Mb(item)) ?? [];
  };

  try {
    await loadData();
  } catch (error) {
    message.error({
      content: error,
      duration: 2,
    });
    logger.debug('getDashboardData', error);
  }

  const series = [
    { name: '平均值', data: dash.avg },
    { name: 'P25', data: dash.p25 },
    { name: 'P50', data: dash.p50 },
    { name: 'P75', data: dash.p75 },
    { name: 'P90', data: dash.p90 },
    { name: 'P99', data: dash.p99 },
  ];
  const countUnit = formatCount(count);
  const unit = 'MB';
  return getMetricEChartOptions(
    series,
    unit,
    countUnit.count,
    countUnit.unit,
    timePoints,
    timeWindow,
    undefined,
    ['#0A6CFF', '#5C61FF', '#00C788', '#F57E22', '#FAC228', '#BE41F0', '#28A2FA'],
    { bottom: 20 },
    showCount,
    ['#0A6CFF', '#5C61FF', '#00C788', '#F57E22', '#FAC228', '#BE41F0', '#28A2FA'],
  );
};


export type DisplayItem = {
  tabName: string;
  metricName: MetricName;
  unit?: string;
  type?: string
};

export const getChartOptionStatsForCombine = async (
  chartData: IGetDashboardDataRsp,
  displayGroups: DisplayItem[],
  timeWindow: TimeType,
  unit?: number,
) => {
  const defaultZero = '0';
  const seriesData = displayGroups.map((group) => {
    const affectCount = chartData?.data?.find(item => item.metricName === group.metricName)?.dataPoints || [];
    if (group.unit) {
      return affectCount.map((item: number) => {
        if (item > 0) {
          const rate = RATE[unit!];
          return ((item * rate)).toFixed(4);
        }
        return defaultZero;
      });
    }
    return affectCount.map(item => (item > 0 ? String(item) : defaultZero));
  });

  const series = displayGroups.map((group, index) => ({
    name: group.tabName,
    data: seriesData[index],
    type: group.type,
    yAxisIndex: index,
    barMaxWidth: '60px',
    smooth: timeWindow !== TimeType.MINUTE5 && group.type === 'line',
    endLabel: {
      position: 'top',
      distance: -18,
      offset: [0, -10],
      show: true,
      formatter: (params: any) => {
        const date = moment(Number(params.name), 'X');
        const momentDate = moment();
        if (timeWindow === TimeType.WEEK) {
          const weekDate = moment(params.name).add(6, 'days');
          if (params.dataIndex === seriesData[index].length - 1 && weekDate.isSameOrAfter(momentDate, 'day')) {
            return '统计中';
          }
        }
        if (timeWindow === TimeType.MONTH) {
          if (params.dataIndex === seriesData[index].length - 1 && date.isSameOrAfter(momentDate, 'month')) {
            return '统计中';
          }
        }
        if (params.dataIndex === seriesData[index].length - 1 && momentDate.isSame(date, 'day')) {
          if (timeWindow === TimeType.DAY) {
            return '统计中';
          }
          if (timeWindow === TimeType.HOUR && momentDate.isSame(date, 'hour')) {
            return '统计中';
          }
          if (timeWindow === TimeType.MINUTE5) {
            let minutes: string | string[] = momentDate.format('mm').split('');
            minutes = `${minutes[0]}${Number(minutes?.[1]) >= 5 ? '5' : '0'}`;
            const startsTime = moment(momentDate.format(`YYYY-MM-DD HH:${minutes}:00`));
            if (date.isSame(startsTime)) {
              return '统计中';
            }
            return '';
          }
        }

        return '';
      },
    },
  }));

  const yAxis = displayGroups.map(group => ({
    type: 'value',
    name: group.tabName,
    splitNumber: DEFUALT_SPLIT_NUMBER,
    alignTicks: true,
    axisLabel: {
      formatter: !group.unit ? '{value}' : `{value}${SYMBOL[unit!]}`,
      textStyle: {
        color: '#7B889C',
      },
    },
    position: group.type === 'bar' ? 'left' : 'right',
  }));

  const xAxisData = (chartData?.timePoints || []).map(timePoint => fromPBTime(timePoint).unix()) || [];
  const legendData =  displayGroups.map(group => group.tabName);
  return {
    tooltip: {
      trigger: 'axis',
      confine: true,
      axisPointer: {
        show: true,
        type: 'line',
        lineStyle: {
          width: 16,
          type: 'solid',
          color: 'rgba(194, 218, 255, 0.5)',
          shadowStyle: {
            color: 'rgba(194, 218, 255, 0.5)',
          },
        },
      },
      formatter(params: any) {
        const isTimeTypeHour = TimeType.HOUR === timeWindow;

        const momentDate = moment.unix(params[0]?.name);
        let str = '';
        if (timeWindow === TimeType.WEEK) {
          str = `${momentDate.format('MM-DD')}~${momentDate.add(6, 'days')
            .format('MM-DD')}</br>
            ${params[0].marker}${params[0].seriesName}：${params[0]?.seriesType === 'bar' ? formatNum(params[0]?.data) : params[0]?.data + SYMBOL[unit!]}</br>
            ${params[1].marker}${params[1].seriesName}：${params[1]?.seriesType === 'bar' ? formatNum(params[1]?.data) : params[1]?.data + SYMBOL[unit!]}`;
        } else if (timeWindow === TimeType.MONTH) {
          str = `${momentDate.format('YY-MM')}</br>
            ${params[0].marker}${params[0].seriesName}：${params[0]?.seriesType === 'bar' ? formatNum(params[0]?.data) : params[0]?.data + SYMBOL[unit!]}</br>
            ${params[1].marker}${params[1].seriesName}：${params[1]?.seriesType === 'bar' ? formatNum(params[1]?.data) : params[1]?.data + SYMBOL[unit!]}`;
        } else {
          str = `${TimeType.DAY === timeWindow ? momentDate.format('YYYY-MM-DD') : `统计区间：[${momentDate.format('HH:mm')}, ${momentDate.add(isTimeTypeHour ? 1 : 5, isTimeTypeHour ? 'hours' : 'minutes').format('HH:mm')})`}</br>
            ${params[0].marker}${params[0].seriesName}：${params[0]?.seriesType === 'bar' ? formatNum(params[0]?.data) : params[0]?.data + SYMBOL[unit!]}</br>
            ${params[1].marker}${params[1].seriesName}：${params[1]?.seriesType === 'bar' ? formatNum(params[1]?.data) : params[1]?.data + SYMBOL[unit!]}`;
        }
        return  str;
      },
    },
    xAxis: {
      data: xAxisData,
      axisLabel: {
        formatter(value: string) {
          switch (timeWindow) {
            case TimeType.DAY:
              return moment.unix(Number(value)).format('MM-DD');
            case TimeType.HOUR:
              return moment.unix(Number(value)).format('HH');
            case TimeType.WEEK:
              return `${moment.unix(Number(value)).format('MM-DD')}~${moment.unix(Number(value)).add(6, 'days')
                .format('MM-DD')}`;
            case TimeType.MONTH:
              return moment.unix(Number(value)).format('YY-MM');
            default:
              return moment.unix(Number(value)).format('HH:mm');
          }
        },
      },
    },
    yAxis,
    grid: {
      left: 10,
      right: 30,
      containLabel: true,
    },
    series,
    legend: {
      bottom: 0,
      data: legendData,
    },
  };
};

